// Domain types for the CheckList DynamoDB single-table model (PLAN.md §4.1).

export type ChecklistRole = "read" | "write";

/** The three shareable resource kinds (SHARING_COLLABORATION_PLAN.md S-1). */
export type ResourceKind = "list" | "template" | "checklist";

export const RESOURCE_KINDS = ["list", "template", "checklist"] as const;

/** Path segment -> kind. "lists" -> "list". Unknown -> null. */
export function resourceKindFromSegment(segment: string): ResourceKind | null {
  if (segment === "lists") return "list";
  if (segment === "templates") return "template";
  if (segment === "checklists") return "checklist";
  return null;
}

/** Kind -> the plural path segment already in use by the API routes. */
export function resourceKindToSegment(kind: ResourceKind): string {
  return kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
}

/** Partition prefix: "LIST" | "TMPL" | "CHK". */
export function kindPrefix(kind: ResourceKind): string {
  return kind === "list" ? "LIST" : kind === "template" ? "TMPL" : "CHK";
}

export interface Profile {
  screenName: string;
  screenNameLower: string;
  email: string;
  /** Optional legal-name parts. When BOTH are set, the colored-initials avatar
   * uses first-initial + last-initial instead of the first two screen-name chars (D-7). */
  firstName?: string;
  lastName?: string;
  /** User-chosen colored-initials background; one of AVATAR_COLORS. Falls back to a
   * hash of the screen name when unset (D-7). */
  avatarColor?: string;
  avatarKey?: string;
  googlePicture?: string;
  createdAt: string;
  /** Set on screenName / avatar mutations; used for CloudFront avatar cache bust. */
  updatedAt?: string;
}

/** Selectable colored-initials avatar backgrounds (D-7). White text is legible on every one. */
export const AVATAR_COLORS = [
  "#0f766e",
  "#0369a1",
  "#b45309",
  "#be123c",
  "#4d7c0f",
  "#0e7490",
  "#c2410c",
  "#1d4ed8",
  "#7c3aed",
  "#a21caf",
  "#475569",
  "#9f1239",
] as const;

export type AvatarColor = (typeof AVATAR_COLORS)[number];

export function isAvatarColor(value: unknown): value is AvatarColor {
  return (
    typeof value === "string" && (AVATAR_COLORS as readonly string[]).includes(value)
  );
}

/** List / template / checklist purpose (PLAN.md D-29). Missing on read → "packing". */
export type ListKind = "packing" | "todo";

/**
 * Fixed community category vocabulary (D-78). A list or template is in a
 * category when its tags contain the slug. No separate attribute.
 */
export const COMMUNITY_CATEGORIES = [
  { slug: "travel", label: "Travel & Outdoors" },
  { slug: "home", label: "Home" },
  { slug: "family", label: "Family & Pets" },
  { slug: "events", label: "Events & Hosting" },
  { slug: "work", label: "Work" },
  { slug: "fitness", label: "Health & Fitness" },
] as const;

export type CommunityCategory = (typeof COMMUNITY_CATEGORIES)[number]["slug"];

export function isCommunityCategory(v: unknown): v is CommunityCategory {
  return (
    typeof v === "string" &&
    COMMUNITY_CATEGORIES.some((category) => category.slug === v)
  );
}

export interface ListItem {
  id: string;
  text: string;
  note?: string;
}

/** One-level subgroup under a List (PLAN.md D-29). */
export interface ListSubgroup {
  id: string;
  name: string;
  items: ListItem[];
}

export interface List {
  name: string;
  nameLower: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  items: ListItem[];
  subgroups: ListSubgroup[];
  isPublished: boolean;
  copyCount: number;
  aiGenerated?: boolean;
  sourceListId?: string;
  ownerId: string;
  ownerScreenName: string;
  createdAt: string;
  updatedAt: string;
  /** Optimistic-concurrency counter. Missing on read -> treat as 0 (no backfill). */
  version?: number;
  /** Denormalized non-revoked share-link count. WS fan-out gate ONLY, never authorization (S-17). */
  activeShareCount?: number;
}

export type TemplateSection =
  | { type: "list"; listId: string; name: string }
  | { type: "inline"; name: string; items: Array<{ id: string; text: string }> };

export interface Template {
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  sections: TemplateSection[];
  createdAt: string;
  updatedAt: string;
  /** Optimistic-concurrency counter. Missing on read -> treat as 0 (no backfill). */
  version?: number;
  /** Denormalized non-revoked share-link count. WS fan-out gate ONLY, never authorization (S-17). */
  activeShareCount?: number;
}

export interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
  checkedBy?: string;
  checkedAt?: string;
  /** Display name for anonymous guests, who have no profile to look up (S-14). */
  screenName?: string;
}

/** One-level subgroup under a ChecklistSection (PLAN.md D-29). */
export interface ChecklistSubgroup {
  id: string;
  name: string;
  items: ChecklistItem[];
}

export interface ChecklistSection {
  name: string;
  items: ChecklistItem[];
  subgroups: ChecklistSubgroup[];
}

export interface Checklist {
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  createdFromTemplateId?: string;
  sections: ChecklistSection[];
  memberCount: number;
  createdAt: string;
  updatedAt: string;
  /** Optimistic-concurrency counter. Missing on read -> treat as 0 (no backfill). */
  version?: number;
  /** Denormalized non-revoked share-link count. WS fan-out gate ONLY, never authorization (S-17). */
  activeShareCount?: number;
  /** ISO-8601 archive timestamp; missing means the checklist is active (D-58). */
  archivedAt?: string;
}

export interface Member {
  role: ChecklistRole;
  screenName: string;
  addedAt: string;
}

export interface Invite {
  checklistId: string;
  role: ChecklistRole;
  createdBy: string;
  expiresAt: string;
  /** DynamoDB TTL attribute (epoch seconds). */
  ttl: number;
  uses: number;
  maxUses: number;
}

export interface EmailGrant {
  role: ChecklistRole;
  createdBy: string;
  createdAt: string;
}

/**
 * Anonymous capability link (SHARING_COLLABORATION_PLAN.md §4.3, S-17).
 * Every permission the sharer chose lives here, on the link — not on the resource.
 */
export interface ShareLink {
  resourceKind: ResourceKind;
  resourceId: string;
  role: ChecklistRole;
  requireSignIn: boolean;
  label?: string;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  /** DynamoDB TTL attribute (epoch seconds); set only when expiresAt is. */
  ttl?: number;
  revokedAt?: string;
  useCount: number;
  lastUsedAt?: string;
}

/** Pending email invitation tied to a share link (SHARING_COLLABORATION_PLAN.md §4.3). */
export interface EmailInvitation {
  role: ChecklistRole;
  shareToken: string;
  invitedBy: string;
  invitedByName: string;
  createdAt: string;
  emailDelivered: boolean;
  /** DynamoDB TTL attribute (epoch seconds), 90d. */
  ttl: number;
}

export type AccessRequestStatus = "pending" | "approved" | "denied";

/** Request for access from someone who scanned a QR they cannot open. */
export interface AccessRequest {
  requesterEmail: string;
  requesterName?: string;
  message?: string;
  status: AccessRequestStatus;
  decisionToken: string;
  decidedAt?: string;
  decidedRole?: ChecklistRole;
  createdAt: string;
  /** DynamoDB TTL attribute (epoch seconds), 30d. */
  ttl: number;
}

export interface Settings {
  /**
   * List-generation model id, on either provider (ANTHROPIC_API_PROVIDER_PLAN
   * D1). The field keeps its historical name: renaming it would touch the CONFIG
   * item and the web contract for no gain.
   */
  bedrockModelId: string;
  aiDailyLimit: number;
  /** Chat assistant model, separate from bedrockModelId (D-A11). */
  aiChatModelId: string;
  /** Chat assistant tokens per user per day (D-A12). */
  aiChatDailyTokenLimit: number;
  /** Whether stored chat transcripts keep text or only usage metadata (SA15, D-R12). */
  aiChatTranscriptMode: AiChatTranscriptMode;
}

/** GET /admin/settings: the settings plus whether the Anthropic API key is set (D15). Never the key. */
export type AdminSettingsResponse = Settings & { anthropicKeyConfigured: boolean };

/** SA15 runtime setting (D-R12): "full" keeps redacted text; "metadata-only" keeps none. */
export type AiChatTranscriptMode = "full" | "metadata-only";

/** Default Bedrock model id (PLAN.md D-18). */
export const DEFAULT_BEDROCK_MODEL_ID = "anthropic.claude-haiku-4-5";

/** Admin-selectable Bedrock model ids (PLAN.md D-18). */
export const ALLOWED_BEDROCK_MODEL_IDS = [
  "anthropic.claude-haiku-4-5",
  // Sonnet 4.6 is the strongest model this account can currently invoke. Sonnet 5
  // is kept on the picker so admins can test access: get-foundation-model-availability
  // can report AVAILABLE/AUTHORIZED while InvokeModel still 403s "not available
  // for this account". Do not alias sonnet-5 to 4.5 -- the label must stay honest.
  "anthropic.claude-sonnet-4-6",
  "anthropic.claude-sonnet-5",
  "amazon.nova-micro-v1:0",
  "amazon.nova-pro-v1:0",
] as const;

export type AllowedBedrockModelId = (typeof ALLOWED_BEDROCK_MODEL_IDS)[number];

/** Read-side and write-side validation share this one check (SA13). */
export function isAllowedBedrockModelId(id: string): id is AllowedBedrockModelId {
  return (ALLOWED_BEDROCK_MODEL_IDS as readonly string[]).includes(id);
}

/** First-party Anthropic API models (ANTHROPIC_API_PROVIDER_PLAN D3). Bare ids, no date suffix. */
export const ALLOWED_ANTHROPIC_MODEL_IDS = ["claude-opus-5-5", "claude-sonnet-5-5", "claude-haiku-5-5"] as const;
export type AllowedAnthropicModelId = (typeof ALLOWED_ANTHROPIC_MODEL_IDS)[number];

/** Every admin-selectable AI model id, either provider (ANTHROPIC_API_PROVIDER_PLAN D4). */
export const ALLOWED_AI_MODEL_IDS = [...ALLOWED_BEDROCK_MODEL_IDS, ...ALLOWED_ANTHROPIC_MODEL_IDS] as const;
export type AllowedAiModelId = (typeof ALLOWED_AI_MODEL_IDS)[number];

/** Read-side and write-side validation for either provider's model id (SA13). */
export function isAllowedAiModelId(id: string): id is AllowedAiModelId {
  return (ALLOWED_AI_MODEL_IDS as readonly string[]).includes(id);
}

/** One daily aggregate returned by the admin AI-cost dashboard. */
export interface AiCostUsageRow {
  date: string;
  modelId: string;
  surface: "generation" | "chat";
  requestCount: number;
  cachedListCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  estimatedCostUsd: number;
  cacheSavingsUsd: number;
}

export interface AdminAiCostUser {
  sub: string;
  screenName: string;
  email: string;
  rows: AiCostUsageRow[];
}

export interface AdminAiCostUsageResponse {
  users: AdminAiCostUser[];
}

/** Default AI generations per user per day (PLAN.md D-19). */
export const DEFAULT_AI_DAILY_LIMIT = 10;

/** PreSignUp verdict recorded on every sign-up event (SIGNUP_ABUSE_DEFENSE_PLAN.md). */
export type SignupOutcome =
  | "allowed"
  | "allowed_allowlist"
  | "allowed_approved"
  | "rejected_turnstile"
  | "rejected_address"
  | "rejected_suppressed"
  | "rejected_duplicate"
  | "rejected_mode"
  | "rejected_breaker";

/** "attempt" from PreSignUp, "confirmed" from PostConfirmation_ConfirmSignUp. */
export type SignupEventKind = "attempt" | "confirmed";

/** Where a sign-up came from: native form, federated IdP, or admin bootstrap. */
export type SignupSource = "native" | "federated" | "admin";

/** Sign-up circuit-breaker status; an absent STATE item means "armed". */
export type SignupGuardStatus = "armed" | "tripped";

/** One SIGNUPLOG row, as the admin Sign-ups dashboard reads it. */
export interface AdminSignupEvent {
  at: string;
  kind: SignupEventKind;
  outcome?: SignupOutcome;
  source: SignupSource;
  email: string;
  /** True for rows reconstructed by the CloudWatch/Cognito backfill. */
  approx?: boolean;
}

/**
 * Admin Sign-ups dashboard payload (SIGNUP_ABUSE_DEFENSE_PLAN.md Task 3.1).
 * `events` are ascending by `at` and capped at 5000. `waitlist` is filled in Session 4.
 */
export interface AdminSignupStats {
  accounts: { total: number; confirmed: number; unconfirmed: number; federated: number };
  events: AdminSignupEvent[];
  guard: {
    status: SignupGuardStatus;
    signupMode: string;
    trippedAt?: string;
    tripReason?: string;
    tripsInWindow: number;
    reopenEligibleAt: string | null;
    lastAttemptAt?: string;
    lastReopenedAt?: string;
  };
  waitlist?: { pending: number; approved: number; denied: number };
}

/** Waitlist record status (SIGNUP_ABUSE_DEFENSE_PLAN.md D-9). */
export type WaitlistStatus = "PENDING" | "APPROVED" | "DENIED";

/** One admin Access-requests row, with IP cluster counts computed from the listed set. */
export interface AdminWaitlistEntry {
  email: string;
  status: WaitlistStatus;
  requestedAt: string;
  firstRequestedAt: string;
  requestCount: number;
  note?: string;
  ip?: string;
  userAgent?: string;
  /** Other rows in this response that share this exact IP. */
  sameIpCount: number;
  /** Other rows in this response that share this IPv4 /24 or IPv6 /48. */
  sameNetworkCount: number;
  approvedAt?: string;
  deniedAt?: string;
  invitedAt?: string;
}

export interface AdminWaitlistResponse {
  entries: AdminWaitlistEntry[];
}

/** Public sign-up form switch. Unknown SSM values are reported as "closed". */
export interface AuthConfigResponse {
  signupMode: "open" | "invite-only" | "closed";
}

/** Fixed system user sub for AI-published community copies (PLAN.md §4.4). */
export const SYSTEM_USER_SUB = "SYSTEM";

/** Sizing constants (PLAN.md §4.1) — stay under DynamoDB 400KB item limit. */
export const LIMITS = {
  /** List, template and checklist names. */
  nameMax: 120,
  listItemsMax: 200,
  itemTextMax: 200,
  itemNoteMax: 500,
  templateSectionsMax: 30,
  checklistItemsMax: 500,
  screenNameMax: 40,
  firstNameMax: 40,
  lastNameMax: 40,
  aiItemTextMax: 100,
  aiItemsMax: 50,
  purposeMin: 3,
  purposeMax: 120,
  subgroupsMax: 20,
  subgroupNameMax: 120,
  shareLinksPerResourceMax: 20,
  shareLinkLabelMax: 60,
  shareLinkMaxExpiryDays: 365,
  accessRequestMessageMax: 500,
  accessRequestsPerEmailPerDay: 10,
  pendingAccessRequestsPerResourceMax: 25,
  guestDisplayNameMax: 40,
  /** Hard cap for the presigned avatar POST (a 256px JPEG is typically 20-60KB). */
  avatarMaxBytes: 1_048_576,
} as const;

/**
 * nameLower normalization (PLAN.md §4.1):
 * trim → collapse internal whitespace to single spaces → toLowerCase()
 */
export function normalizeNameLower(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Normalize raw kind; unknown / missing → "packing" (PLAN.md D-29). */
export function normalizeListKind(raw: unknown): ListKind {
  return raw === "todo" ? "todo" : "packing";
}
